import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Alert from '../common/Alert.js';
import attendanceService from '../../services/AttendanceService.js';
import userService from '../../services/UserService.js';
import axiosInstance from '../../config/axios.js';
import { Calendar, ArrowLeft, Eye, Pencil, Trash2 } from 'lucide-react';
import {
  PageLayout,
  Button,
  DataTable,
  StatusBadge,
  DetailCard,
  DetailRow,
} from '../common/index.js';

const ViewAttendanceReport = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  useEffect(() => {
    fetchData();
  }, [userId, selectedMonth, selectedYear]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [userRes, reportRes] = await Promise.all([
        userService.getById(userId),
        attendanceService.getUserMonthlyReport(userId, selectedYear, selectedMonth),
      ]);
      setUser(userRes.data);
      setMonthlyReport(reportRes.data);
    } catch (err) {
      Alert.error('Failed to load attendance report');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (attendanceId, attendanceDate) => {
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete the attendance record for ${formatDate(attendanceDate)}? This action cannot be undone.`,
      'Delete Attendance Record'
    );

    if (confirmed) {
      try {
        await axiosInstance.delete(`/attendance/${attendanceId}`);
        Alert.success('Attendance record deleted successfully!');
        fetchData();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete attendance record');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getAttendancePercentage = () => {
    if (!monthlyReport) return 0;
    return ((monthlyReport.days_worked / monthlyReport.total_days_in_month) * 100).toFixed(1);
  };

  const columns = [
    {
      key: 'attendance_date',
      label: 'Date',
      render: (record) => formatDate(record.attendance_date),
    },
    {
      key: 'login_time',
      label: 'Login Time',
      render: (record) => formatTime(record.login_time),
    },
    {
      key: 'logout_time',
      label: 'Logout Time',
      render: (record) =>
        record.is_logged_out ? formatTime(record.logout_time) : <StatusBadge status="active" label="Active" />,
    },
    { key: 'work_hours_formatted', label: 'Work Hours' },
    {
      key: 'login_location',
      label: 'Login Location',
      render: (record) =>
        record.login_location
          ? `${record.login_location.city}, ${record.login_location.state}`
          : 'N/A',
    },
    {
      key: 'logout_location',
      label: 'Logout Location',
      render: (record) =>
        record.logout_location
          ? `${record.logout_location.city}, ${record.logout_location.state}`
          : 'N/A',
    },
    {
      key: 'status',
      label: 'Status',
      render: (record) => (
        <StatusBadge status={record.is_logged_out ? 'completed' : 'active'} />
      ),
    },
  ];

  const getActions = (record) => [
    {
      label: 'View Details',
      icon: Eye,
      onClick: () => navigate(`/attendance/view/${record.id}`),
    },
    {
      label: 'Edit',
      icon: Pencil,
      onClick: () => navigate(`/attendance/edit/${record.id}`),
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger',
      onClick: () => handleDelete(record.id, record.attendance_date),
    },
  ];

  if (loading) {
    return (
      <PageLayout title="Attendance Report" icon={Calendar}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-gray-600">Loading attendance report...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Attendance Report"
      icon={Calendar}
      actions={
        <>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}
            className="input-clean text-sm"
          >
            {monthNames.map((month, index) => (
              <option key={index + 1} value={index + 1}>
                {month}
              </option>
            ))}
          </select>
          <input
            type="number"
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
            min="2020"
            max="2099"
            className="input-clean text-sm w-24"
          />
          <Button variant="outline" icon={ArrowLeft} onClick={() => navigate('/attendance')}>
            Back
          </Button>
        </>
      }
    >
      <p className="text-sm text-gray-600 mb-4">{user?.name}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <DetailCard>
          <DetailRow label="Total Days" value={monthlyReport?.total_days_in_month} />
        </DetailCard>
        <DetailCard>
          <DetailRow label="Days Worked" value={monthlyReport?.days_worked} />
        </DetailCard>
        <DetailCard>
          <DetailRow label="Days Absent" value={monthlyReport?.days_absent} />
        </DetailCard>
        <DetailCard>
          <DetailRow label="Total Hours" value={monthlyReport?.total_work_hours_formatted} />
        </DetailCard>
      </div>

      <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-card shadow-card mb-6">
        <p className="text-blue-100 text-sm mb-1">Attendance Rate</p>
        <p className="text-4xl font-bold">{getAttendancePercentage()}%</p>
        <div className="mt-4 bg-white/20 rounded-full h-2">
          <div
            className="bg-white h-2 rounded-full transition-all duration-500"
            style={{ width: `${getAttendancePercentage()}%` }}
          />
        </div>
      </div>

      <DetailCard title="Daily Attendance Records">
        <DataTable
          columns={columns}
          data={monthlyReport?.attendance_records || []}
          loading={false}
          emptyMessage="No attendance records for this month"
          rowKey="id"
          actions={getActions}
          className="shadow-none"
        />
      </DetailCard>
    </PageLayout>
  );
};

export default ViewAttendanceReport;
